import i18next from '@/config/i18n';
import eventBus from '@/eventBus';
import { useSnackbar } from '@/composables/useSnackbar';

// ──────────────────────────────────────────────────────────────────────────
// RF-T4.8 (D9) — aviso de conclusão da extração do Brand DNA COM O PAINEL
// FECHADO, que é o que torna honesta a promessa do RF-T3.1 ("pode fechar esta
// janela").
//
// Por que isto não mora no `CompanyBrandGuidePanel`: os dois hosts destroem o
// painel ao fechar (`v-if="currentFolder"` no `BrandKitEditDialog`, `v-if` no
// `_brandEditorFolder` do `BulkUploadConfirmation`), então a instância que
// começou a extração já não existe quando o `done` chega. O ouvinte precisa
// viver ACIMA dela, e aqui vive: o `App.vue` monta uma vez por sessão e arma o
// `startBrandKitNotices` abaixo, que vale em qualquer rota.
//
// Diferença deliberada para o registry do `usePptxExportStatus`: lá a entrada é
// ARMADA ANTES, no clique, e reconstruída de `/reports/my-queue` depois de um
// F5. Aqui não existe fila a consultar (um endpoint de "extrações em andamento"
// devolveria vazio justamente no caso que interessa, porque a essa altura o kit
// já é `done`), então quem arma o estado é a PRÓPRIA mensagem que chega: ela é
// auto-descritiva (`folder_id` + `stage`), que é tudo de que um aviso precisa.
//
// O que isto fecha: modal fechado, outra rota, e também depois de um reload,
// porque o evento sai no canal do usuário (`publish_to_user`) e o `useWebSocket`
// segue aberto. O que NÃO fecha, e não finge fechar: a aba fechada no instante
// da conclusão. O pub/sub do Redis não guarda histórico e não há carimbo de
// "ainda não avisado" no kit, então na volta não existe o que entregar. Isso é
// caixa de entrada (coluna nova + migration), item próprio no plano (G7).
// ──────────────────────────────────────────────────────────────────────────

// O padrão do snackbar é 3s, calibrado para quem acabou de clicar em alguma
// coisa. Este aviso chega para quem está em OUTRA tela e talvez nem lembrasse
// que havia algo rodando, então precisa de mais tempo em tela. Mesmo valor que
// o `usePptxExportStatus` usa nos avisos que não nascem de um clique.
const NOTICE_TIMEOUT_MS = 7000;

// Pastas cujo painel de Brand DNA está montado AGORA.
//
// É assim que o aviso evita dizer duas vezes a mesma coisa: com o painel aberto
// NAQUELA pasta, o desfecho já aparece na própria tela (o polling de 5s aplica a
// proposta e os campos ganham a marca "Sugestão da IA"; no `failed`, o bloco de
// erro toma o lugar do spinner), e um snackbar por cima seria o mesmo recado
// duplicado. Com o painel aberto em OUTRA pasta o aviso sai normalmente: aquela
// tela não fala do kit que terminou.
//
// Set, e não contador por pasta: dois painéis da MESMA pasta ao mesmo tempo não
// é estado real (cada host abre um diálogo por vez), e o pior que um empate
// causaria é um aviso a menos, nunca um aviso duplicado.
const openPanels = new Set();

export function registerOpenBrandKitPanel(folderId) {
  openPanels.add(String(folderId));
}

export function unregisterOpenBrandKitPanel(folderId) {
  openPanels.delete(String(folderId));
}

// `done` e `failed` são os únicos estágios que viram aviso. As fronteiras
// intermediárias do contrato (`text_done`, `logos_detected`, `logo_ready`) são
// narração de progresso: fazem sentido na tela que mostra a barra, e viradas
// snackbar seriam quatro interrupções por extração.
function handleWsMessage(data) {
  if (!data || data.type !== 'brand_kit_progress') return;
  if (data.stage !== 'done' && data.stage !== 'failed') return;
  if (openPanels.has(String(data.folder_id))) return;

  const { showSnackbar } = useSnackbar();
  if (data.stage === 'done') {
    showSnackbar(
      i18next.t('components.admin.companyBrandGuidePanel.snackbar.extractionDone'),
      'success',
      NOTICE_TIMEOUT_MS,
    );
  } else {
    showSnackbar(
      i18next.t('components.admin.companyBrandGuidePanel.snackbar.extractionFailed'),
      'error',
      NOTICE_TIMEOUT_MS,
    );
  }
}

// Idempotente nas duas pontas. O `App.vue` é único, mas o eventBus é um mitt
// global: um remount que registrasse de novo deixaria dois ouvintes vivos e
// cada conclusão viraria dois snackbars.
let listening = false;

export function startBrandKitNotices() {
  if (listening) return;
  eventBus.on('websocket-message', handleWsMessage);
  listening = true;
}

export function stopBrandKitNotices() {
  if (!listening) return;
  eventBus.off('websocket-message', handleWsMessage);
  listening = false;
}
